import React, { lazy } from 'react'
import { Route, useNavigate } from 'react-router-dom'
import { Provider as JotaiProvider } from 'jotai'
import {
  editorCommentStore
} from '../AppJotaiStores'
import showPaywall from '../services/show-paywall'
import { currentRouteFeature } from '../services/feature-from-route'
import { AuthContext } from '../context/firebase-context'
import { canAccessSceneWorkflow } from '../modules/Storyboard/sceneWorkflowAccess'

const SharedEditor = lazy(() => import('../modules/SharedProject/Editor/EditorContainer'))
const SharedStoryboard = lazy(() => import('../modules/SharedProject/Storyboard/StoryboardContainer'))
const SharedSceneWorkflow = lazy(() => import('../modules/SharedProject/Storyboard/SceneWorkflowContainer'))
const SharedScriptBreakdown = lazy(() => import('../modules/SharedProject/ScriptBreakdown/ScriptBreakdownContainer'))
const SharedProjectVerify = lazy(() => import('../modules/SharedProject/Verify/VerifyContainer'))
const SingleProject = lazy(() => import('../modules/Projects/containers/SingleProjectContainer'))
const Scheduling = lazy(() => import('../modules/SharedProject/Scheduling/SchedulingContainer'))
const Budgeting = lazy(() => import('../modules/SharedProject/Budgeting/BudgetingContainer'))
const BudgetWizard = lazy(() => import('../modules/Budgeting/containers/BudgetingWizardContainer'))
const SharedPitchDeck = lazy(() => import('../modules/SharedProject/PitchDeck/SharedPitchDeckContainer'))

const SceneWorkflowProGuard = ({ children }) => {
  const user = React.useContext(AuthContext)
  const navigate = useNavigate()
  const shownRef = React.useRef(false)
  const blockedByProPlan = !canAccessSceneWorkflow(user)

  React.useEffect(() => {
    if (blockedByProPlan && !shownRef.current) {
      shownRef.current = true
      // See EditorRoutes: the feature has to be read before the redirect.
      const featureName = currentRouteFeature()
      navigate('/dashboard')
      showPaywall('pro', { paywallType: 'feature', featureName, trigger: 'route_guard' })
    }
  }, [blockedByProPlan, navigate])

  if (blockedByProPlan) return null
  return children
}

export const sharedProjectRoutes = [
  <Route
    key='shared-editor'
    path='/shared_project/verify/:token'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SharedProjectVerify />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-project'
    path='/shared_project/:projectSlug'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SingleProject />
      </JotaiProvider>
    }
  />,
  <Route key='shared-project-feature' path='/shared_project/:projectSlug/:featureName' element={<SingleProject />} />,
  <Route
    key='shared-editor'
    path='/shared_project/:projectSlug/editor/:scriptUuid'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SharedEditor />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-storyboard'
    path='/shared_project/:projectSlug/storyboard/:scriptUuid'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SharedStoryboard />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-scene-workflow'
    path='/shared_project/:projectSlug/storyboard/:scriptUuid/workflow'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SceneWorkflowProGuard>
          <SharedSceneWorkflow />
        </SceneWorkflowProGuard>
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-script-breakdown'
    path='/shared_project/:projectSlug/breakdown/:scriptUuid'
    element={
      <JotaiProvider store={editorCommentStore}>
        <SharedScriptBreakdown />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-script-breakdown'
    path='/shared_project/:projectSlug/scheduling/:scriptUuid'
    element={
      <JotaiProvider store={editorCommentStore}>
        <Scheduling />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-budget-wizard'
    path='/shared_project/:projectSlug/budgeting-forecast/wizard/:slug'
    element={
      <JotaiProvider store={editorCommentStore}>
        <BudgetWizard />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-budgeting'
    path='/shared_project/:projectSlug/budgeting-forecast/:slug'
    element={
      <JotaiProvider store={editorCommentStore}>
        <Budgeting />
      </JotaiProvider>
    }
  />,
  <Route
    key='shared-pitch-deck'
    path='/shared_project/:projectSlug/pitch-deck/:slug'
    element={<SharedPitchDeck />}
  />
]
